import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatChartDate } from "@/lib/intelligenceFormat";
import type { FinanceDailyTrendPoint } from "@/api/intelligence";

type Props = {
	dailyTrend: FinanceDailyTrendPoint[];
};

export function FinanceTrendChart({ dailyTrend }: Props) {
	const chartData = useMemo(
		() => ({
			labels: dailyTrend.map((p) => formatChartDate(p.date)),
			datasets: [
				{
					label: "Net revenue (₹)",
					data: dailyTrend.map((p) => p.netRevenue),
					borderColor: "rgb(79, 70, 229)",
					tension: 0.25,
					fill: false,
				},
				{
					label: "Collections (₹)",
					data: dailyTrend.map((p) => p.collections),
					borderColor: "rgb(16, 185, 129)",
					tension: 0.25,
					fill: false,
				},
				{
					label: "Outstanding (₹)",
					data: dailyTrend.map((p) => p.outstanding),
					borderColor: "rgb(245, 158, 11)",
					tension: 0.25,
					fill: false,
					yAxisID: "y1",
				},
			],
		}),
		[dailyTrend]
	);

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: "index" as const, intersect: false },
			plugins: { legend: { position: "bottom" as const } },
			scales: {
				y1: {
					type: "linear" as const,
					display: true,
					position: "right" as const,
					grid: { drawOnChartArea: false },
				},
			},
		}),
		[]
	);

	if (dailyTrend.length === 0) {
		return (
			<Card>
				<CardContent className="py-8 text-center text-sm text-muted-foreground">
					No trend data
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base font-medium">Revenue & collections trend</CardTitle>
				<p className="text-xs text-muted-foreground">
					IST calendar days in selected period · Collections line is cash received that day
					(may include older dues)
				</p>
			</CardHeader>
			<CardContent>
				<div className="h-[280px]">
					<Line data={chartData} options={options} />
				</div>
			</CardContent>
		</Card>
	);
}
